import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbstractControlComponent, CpControlType } from '@se/cp.engine';
import { AutoUnsubscribe } from '@se/platform';
import { Guid } from '@se/se.ui.html5';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CpControlGrid, CpControlGridCell, CpControlGridRow } from '../../cp-configuration/utils/control-grid';

import { CpControlsService } from '../../cp-controls.service';

type BuiltComponent = [CpControlType | null, ComponentRef<AbstractControlComponent>];

@Component({
    selector: 'cp-control-grid',
    templateUrl: './custom-page-control-grid.component.html',
    styleUrls: ['./custom-page-control-grid.component.scss'],
})
export class CustomPageControlGridComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    @ViewChildren('controlGridCell', { read: ViewContainerRef })
    uiControlGridCells!: QueryList<ViewContainerRef>;

    @Input()
    cardId!: string;

    @Input()
    disabled = false;

    @Input()
    form!: FormGroup;

    @Input()
    grid!: CpControlGrid;

    @Output()
    edited = new EventEmitter<void>();

    @Output()
    focused = new EventEmitter<void>();

    @Output()
    warningsChanged = new EventEmitter<string[]>();
    private warningsChangedSubject = new Subject<string[]>();

    @Output()
    autoUpdated = new EventEmitter<boolean>();
    private autoUpdatedSubject = new Subject<boolean>();

    @Output()
    hasAIDataChanged = new EventEmitter<boolean>();
    private hasAIDataSubject = new Subject<boolean>();

    controls: CpControlGridRow[] = [];

    components: BuiltComponent[] = [];

    private id = new Guid().toString();
    private destroyed$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private controlsService: CpControlsService,
    ) {}

    ngOnInit() {
        this.controls = this.grid.toValidArray();
        this.warningsChangedSubject
            .pipe(takeUntil(this.destroyed$), debounceTime(100), distinctUntilChanged())
            .subscribe(this.warningsChanged);
        this.autoUpdatedSubject
            .pipe(takeUntil(this.destroyed$), debounceTime(100), distinctUntilChanged())
            .subscribe(this.autoUpdated);
        this.hasAIDataSubject
            .pipe(takeUntil(this.destroyed$), debounceTime(100), distinctUntilChanged())
            .subscribe(this.hasAIDataChanged);
    }

    ngAfterViewInit() {
        const uiGridCells = this.uiControlGridCells.toArray();
        let gridCellsIter = 0;

        for (const row of this.controls) {
            for (const controlCell of row.cells) {
                const uiCell = uiGridCells[gridCellsIter++];
                if (controlCell && uiCell) {
                    const component = this.createComponent(
                        controlCell,
                        uiCell,
                        row.cells.reduce((sum, cell) => sum + (cell.control?.columnSpan ?? 1), 0),
                    );
                    if (component) {
                        this.components.push([controlCell.control, component] as BuiltComponent);
                    }
                }
            }
        }

        for (const [_, component] of this.components) {
            component.instance.warningChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
                this.warningsChangedSubject.next(this.getAllComponentWarningMessages());
            });

            component.instance.autoUpdated.pipe(takeUntil(this.destroyed$)).subscribe(() => {
                this.autoUpdatedSubject.next(this.isAnyComponentAutoUpdated());
            });

            component.instance.hasAIDataChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
                this.hasAIDataSubject.next(this.hasAnyComponentAIData());
            });

            component.changeDetectorRef.markForCheck();
        }

        this.cdr.detectChanges();

        for (const [control, component] of this.components) {
            if (control) {
                this.controlsService.initialized(component.instance);
            }
        }
    }

    ngOnChanges({ disabled }: SimpleChanges) {
        if (disabled) {
            this.controlsService.byGridId(this.id).forEach((component) => {
                component.instance.isDisabledByBinding = disabled.currentValue;
                component.changeDetectorRef.detectChanges();
            });
        }
    }

    @AutoUnsubscribe()
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.controlsService.destroyGrid(this.id);
    }

    trackIndex(index: number) {
        return index;
    }

    trackCellId(index: number, cell: CpControlGridCell) {
        if (cell.control) {
            return cell.control?.id;
        }

        return index;
    }

    private createComponent(controlCell: CpControlGridCell, viewContainer: ViewContainerRef, numColumns: number) {
        if (!controlCell.control) {
            return;
        }

        const component = this.controlsService.create(viewContainer, controlCell.control.controlTypeGroup, this.id);

        // Inputs
        component.instance.cardId = this.cardId;
        component.instance.control = controlCell.control;
        component.instance.form = this.form;
        component.instance.isDisabledByBinding = this.disabled;
        component.instance.containerInfo = { numColumns };

        // Outputs
        component.instance.focusin.asObservable().pipe(takeUntil(this.destroyed$)).subscribe(this.focused);

        component.instance.edited.pipe(takeUntil(this.destroyed$)).subscribe(this.edited);

        return component;
    }

    private isAnyComponentAutoUpdated() {
        return this.components
            .filter(([, c]) => ('readonly' in c.instance.control ? !c.instance.control.readonly : true))
            .some(([_, c]) => !!c.instance.isAutoUpdated);
    }

    private hasAnyComponentAIData() {
        return this.components.some(([_, c]) => !!c.instance.hasAIData);
    }

    private getAllComponentWarningMessages() {
        return this.components
            .map(([_, c]) => c.instance.warningMessage)
            .filter(((msg) => !!msg) as (x: string | null) => x is string);
    }

    /**
     * /*77398 gestione warning per measure out of range*/
    //const measureOutOfRangeWarnings = warningMessages.filter(message => message === 'MeasureOutOfRange');
    //if (measureOutOfRangeWarnings.length > 0) {
    //    this.measureOutOfRangeWarningsChanged.emit(measureOutOfRangeWarnings);
    //}
}
